import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import '../../CSS/Global.css';
import { getDecryptedUserFromCookie, getSessionMainId } from "../utils/getCookie";
import axios from 'axios'
import { toast } from "react-toastify";
import { v4 as uuidv4 } from 'uuid';
import { decryptToken } from "../utils/getToken";
import { X, Box, Layers, PackagePlus } from "lucide-react";
import InvoiceAddProductFormModal from "./InvoiceAddProductFormModal";
import {
    pricingFromProduct,
    pricingFromVariation,
} from "./invoiceProductPricing";

// utils/calcAmount.js
export const getRowAmount = (row, editMode = false) => {
    const { quantity, rate, discount, tax, amount } = row;

    const userEdited =
        quantity || rate || discount || tax; // detect if user touched fields

    if (!userEdited && editMode && amount !== undefined && amount !== null && amount !== "") {
        // ✅ Trust backend amount only when editing existing invoice and no fields touched
        return parseFloat(amount).toFixed(2);
    }

    // ✅ Otherwise recalc dynamically
    return (
        (parseFloat(quantity) || 0) *
        (parseFloat(rate) || 0) *
        (1 - (parseFloat(discount) || 0) / 100) *
        (1 + (parseFloat(tax) || 0) / 100)
    ).toFixed(2);
};




const itemSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

/** react-select option or raw product from onChange handlers */
function resolveProductFromSelect(value) {
    if (!value) return null;
    if (value.value && typeof value.value === "object") {
        return value.value;
    }
    return value;
}

function rowFromProduct(row, product) {
    const { rate, discount, tax } = pricingFromProduct(product);
    return {
        ...row,
        item: {
            id: product.id,
            title: `${product.title} - ${product.sku}`,
            sku: product.sku,
        },
        rate,
        discount,
        tax,
        quantity: row.quantity && parseFloat(row.quantity) > 0 ? row.quantity : "1",
        hsn: product.hsn || "",
    };
}

function rowFromVariation(row, parentProduct, variation) {
    const { rate, discount, tax } = pricingFromVariation(variation, parentProduct);
    const variationTitle = String(variation?.title || "").trim();
    const itemLabel = variationTitle
        ? `${parentProduct.title} - ${variationTitle}`
        : `${parentProduct.title} - ${variation.sku}`;
    return {
        ...row,
        item: {
            id: parentProduct.id,
            title: itemLabel,
            sku: variation.sku,
        },
        rate,
        discount,
        tax,
        quantity: row.quantity && parseFloat(row.quantity) > 0 ? row.quantity : "1",
        hsn: parentProduct.hsn || "",
    };
}

const mapProductsToOptions = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((product) => product && product.title)
        .map((product) => {
            let variation = product.variation;
            if (typeof variation === "string") {
                try {
                    variation = JSON.parse(variation);
                } catch {
                    variation = [];
                }
            }
            return {
                label: product.title,
                value: {
                    id: product.id,
                    title: product.title,
                    hsn: product.hsn,
                    product_type: product.product_type,
                    sku: product.sku || product.product_id,
                    originalPrice: product.originalPrice,
                    price: product.price,
                    discount_type: product.discount_type,
                    discount_price: product.discount_price,
                    discount_percentage: product.discount_percentage,
                    gst_percentage: product.gst_percentage,
                    variation: Array.isArray(variation) ? variation : [],
                },
            };
        });
};

const ItemTable = ({ items, setItems, setSubtotal, itemsOptions, setItemsOptions }) => {
    const url = API_BASE_URL;
    const isLoggedIn = getDecryptedUserFromCookie();
    const tenantId = getSessionMainId() || isLoggedIn?.main_id || isLoggedIn?.user_id;
    // const [itemsOptions, setItemsOptions] = useState([]);
    const [showVariationModal, setShowVariationModal] = useState(false);
    const [showAddProductModal, setShowAddProductModal] = useState(false);
    const [productFormType, setProductFormType] = useState(null);
    const [currentVariableProduct, setCurrentVariableProduct] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const token = decryptToken("accessToken") || ""

    const fetchServiceData = async () => {
        if (!tenantId) {
            toast.error("Session expired. Please log in again.");
            return;
        }
        if (!token) {
            toast.error("Missing auth token. Please log in again.");
            return;
        }
        try {
            const response = await axios.get(
                `${url}/grouped-product/search-all-products/${encodeURIComponent(tenantId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Cache-Control": "no-cache",
                        Pragma: "no-cache",
                    },
                    params: { _t: Date.now(), includeInactive: true },
                }
            );

            const raw = response.data;
            if (!Array.isArray(raw)) {
                console.error("search-all-products: expected array, got:", raw);
                toast.error("Could not load products for invoice.");
                setItemsOptions([]);
                return;
            }

            const productOptions = mapProductsToOptions(raw);
            setItemsOptions(productOptions);
            if (!productOptions.length) {
                toast.info("No products found. Add products under Products, or use Add product.");
            }
        } catch (error) {
            console.error("Invoice item fetch error:", error);
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load products.";
            toast.error(msg);
            setItemsOptions([]);
        }
    };



    const handleInputChange = (id, field, value) => {
        if (field === "item") {
            const product = resolveProductFromSelect(value);
            if (!product) return;

            if (product.product_type === "Variable Product") {
                setCurrentVariableProduct(product);
                setSelectedRowId(id);
                setShowVariationModal(true);
                return;
            }

            const updatedRows = items.map(row =>
                row.id === id ? rowFromProduct(row, product) : row
            );
            setItems(updatedRows);
            recalculateSubtotal(updatedRows);
            return;
        }

        // Handle other fields (quantity, rate, etc.)
        const updatedRows = items.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        );
        setItems(updatedRows);
        recalculateSubtotal(updatedRows);
    };


    const recalculateSubtotal = (updatedItems) => {
        console.log(updatedItems)
        const newSubtotal = updatedItems.reduce((acc, row) => {
            const qty = parseFloat(row.quantity) || 0;
            const rate = parseFloat(row.rate) || 0;
            const discount = parseFloat(row.discount) || 0;
            const tax = parseFloat(row.tax) || 0;
            return acc + qty * rate * (1 - discount / 100) * (1 + tax / 100);
        }, 0);
        setSubtotal(newSubtotal);
    };


    const handleAddRow = () => {
        setItems([
            ...items,
            { id: uuidv4(), item: null, quantity: "1", rate: "", discount: "", tax: "", hsn: "" },
        ]);
    };

    const handleDeleteRow = (id) => {
        const filtered = items.filter(row => row.id !== id);
        setItems(filtered);
        recalculateSubtotal(filtered);
    };

    // const handleDeleteRow = (indexToRemove) => {
    //     const updatedItems = items.filter((_, index) => index !== indexToRemove);
    //     setItems(updatedItems);
    //     // recalculateSubtotal(updatedItems)
    // };



    useEffect(() => {
        if (tenantId && token) {
            fetchServiceData();
        }
    }, [tenantId, token]);

    return (
        <>
            <div className="item-table-container">
                <table className="item-table">
                    <colgroup>
                        <col className="item-table-col-item" />
                        <col className="item-table-col-hsn" />
                        <col className="item-table-col-qty" />
                        <col className="item-table-col-rate" />
                        <col className="item-table-col-discount" />
                        <col className="item-table-col-tax" />
                        <col className="item-table-col-amount" />
                        <col className="item-table-col-action" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className="item-table-select-item">Items</th>
                            <th className="item-table-col-hsn">HSN</th>
                            <th className="item-table-col-qty">Quantity</th>
                            <th className="item-table-col-rate">Rate</th>
                            <th className="item-table-col-discount">Discount (%)</th>
                            <th className="item-table-col-tax">Tax (%)</th>
                            <th className="item-table-col-amount">Amount</th>
                            <th className="item-table-delete">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row) => (
                            <tr key={row.id}>
                                <td className="item-table-select-item-td">
                                    <Select
                                        options={itemsOptions}
                                        value={
                                            row.item && row.item.id
                                                ? itemsOptions.find(opt => opt.value.id === row.item.id)
                                                : row.item && row.item.title
                                                    ? { label: row.item.title, value: row.item }
                                                    : null
                                        }
                                        onChange={(option) => option && handleInputChange(row.id, "item", option.value)}
                                        placeholder="Select Item"
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={itemSelectStyles}
                                        isClearable
                                        noOptionsMessage={() =>
                                            itemsOptions.length
                                                ? "No matching products"
                                                : "No products — add under Products"
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        value={row.hsn || ""}
                                        readOnly
                                        className="readonly-input"
                                        placeholder="HSN"
                                    />
                                </td>

                                <td>
                                    <input
                                        type="number"
                                        value={row.quantity}
                                        onChange={(e) =>
                                            handleInputChange(row.id, "quantity", e.target.value)
                                        }
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="Quantity"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={row.rate}
                                        onChange={(e) =>
                                            handleInputChange(row.id, "rate", e.target.value)
                                        }
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="Rate"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={row.discount}
                                        step="any"
                                        name={`discount-${row.id}`}
                                        onChange={(e) =>
                                            handleInputChange(row.id, "discount", e.target.value)
                                        }
                                        onWheel={(e) => e.target.blur()}
                                        onBlur={(e) => {
                                            const value = parseFloat(e.target.value);
                                            if (isNaN(value) || value < 0 || value > 100) {
                                                toast.warning("Please enter a valid discount (0–100%)");
                                                handleInputChange(row.id, "discount", "");
                                            } else {
                                                handleInputChange(row.id, "discount", value.toFixed(2));
                                            }
                                        }}

                                        placeholder="Discount"
                                        min="0"
                                        max="100"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={row.tax}
                                        onChange={(e) =>
                                            handleInputChange(row.id, "tax", e.target.value)
                                        }
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="Tax"
                                        min="0"
                                        max="100"
                                    />
                                </td>
                                <td className="item-table-amount">
                                    {/* {row.amount
                                        ? parseFloat(row.amount).toFixed(2)
                                        : (
                                            (row.quantity || 0) *
                                            (row.rate || 0) *
                                            (1 - (parseFloat(row.discount) || 0) / 100) *
                                            (1 + (parseFloat(row.tax) || 0) / 100)
                                        ).toFixed(2)
                                    } */}
                                    {getRowAmount(row, true)}
                                </td>

                                <td className="item-table-delete">
                                    <button
                                        onClick={() => handleDeleteRow(row.id)}
                                        className="delete-btn"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="item-table-actions">
                    <button type="button" onClick={handleAddRow} className="add-row-btn">
                        Add more
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowAddProductModal(true)}
                        className="add-row-btn add-product-row-btn"
                    >
                        Add product
                    </button>
                </div>
            </div>

            <div className="item-table-mobile">
                <ItemTableMobile
                    items={items}
                    setItems={setItems}
                    itemsOptions={itemsOptions}
                    showVariationModal={showVariationModal}
                    setShowVariationModal={setShowVariationModal}
                    currentVariableProduct={currentVariableProduct}
                    setCurrentVariableProduct={setCurrentVariableProduct}
                    setSelectedRowId={setSelectedRowId}
                    onOpenAddProduct={() => setShowAddProductModal(true)}
                />

            </div>
            {showVariationModal && currentVariableProduct && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Select Variation for {currentVariableProduct.title}</h3>
                        {currentVariableProduct.variation.map(v => {
                            const { rate } = pricingFromVariation(v, currentVariableProduct);
                            return (
                            <div key={v.id} className="variation-item" onClick={() => {
                                const updated = items.map(row =>
                                    row.id === selectedRowId
                                        ? rowFromVariation(row, currentVariableProduct, v)
                                        : row
                                );
                                setItems(updated);
                                recalculateSubtotal(updated);
                                setShowVariationModal(false);
                            }}>
                                <img src={`${url}${v.imageUrl}`} alt="variation" width="50" />
                                <div>
                                    {v.title?.trim() ? (
                                        <p className="variation-item-title">{v.title.trim()}</p>
                                    ) : null}
                                    <p>SKU: {v.sku}</p>
                                    <p>MRP: ₹{rate || v.price || v.salePrice}</p>
                                    <p>Stock: {v.stock}</p>
                                </div>
                            </div>
                            );
                        })}
                        <button onClick={() => setShowVariationModal(false)}>Cancel</button>
                    </div>
                </div>
            )}

            <AddProductModal
                isOpen={showAddProductModal}
                onClose={() => setShowAddProductModal(false)}
                onSelectType={(type) => {
                    setShowAddProductModal(false);
                    setProductFormType(type);
                }}
            />

            <InvoiceAddProductFormModal
                productType={productFormType}
                isOpen={Boolean(productFormType)}
                onClose={() => setProductFormType(null)}
                onSuccess={fetchServiceData}
            />

        </>
    );
};

export default ItemTable;

const INVOICE_PRODUCT_TYPES = [
    { name: "Simple Product", icon: Box },
    { name: "Variable Product", icon: Layers },
    { name: "Grouped Product", icon: PackagePlus },
];

const AddProductModal = ({ isOpen, onClose, onSelectType }) => {
    if (!isOpen) return null;

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[9998] bg-black/50"
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                onClick={onClose}
                role="presentation"
            >
                <div
                    className="w-full max-w-[400px] rounded-2xl bg-white p-5 shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="mb-4 pb-2 border-b border-gray-300 flex justify-between items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-800">
                            Create product
                        </h3>
                        <span
                            onClick={onClose}
                            className="cursor-pointer size-8 rounded-full flex justify-center items-center bg-gray-50 text-black hover:bg-gray-200 transition-colors"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") onClose();
                            }}
                            aria-label="Close"
                        >
                            <X size={18} />
                        </span>
                    </div>

                    <div className="flex flex-col gap-3">
                        {INVOICE_PRODUCT_TYPES.map(({ name, icon: Icon }) => (
                            <div
                                key={name}
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelectType?.(name)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        onSelectType?.(name);
                                    }
                                }}
                                className="cursor-pointer flex items-center justify-center gap-3 w-full text-base font-medium text-gray-800 rounded-xl px-4 py-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-all duration-200"
                            >
                                <Icon className="text-blue-600 shrink-0" size={20} />
                                {name}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};






const ItemTableMobile = ({ items,
    setItems,
    itemsOptions,
    showVariationModal,
    setShowVariationModal,
    currentVariableProduct,
    setCurrentVariableProduct,
    setSelectedRowId,
    onOpenAddProduct }) => {
    // const [items, setItems] = useState([
    //     { id: Date.now(), item: null, quantity: "", rate: "", discount: "", tax: "", amount: 0 },
    // ]);
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const [equipments, setEquipments] = useState([]);





    const calculateAmount = (quantity, rate, discount, tax, amount, editMode = false) => {
        const userEdited =
            quantity || rate || discount || tax; // ✅ detect edits

        if (!userEdited && editMode && amount !== undefined && amount !== null && amount !== "") {
            // ✅ Trust backend amount only when editing existing invoice and no fields touched
            return parseFloat(amount).toFixed(2);
        }

        // ✅ Otherwise recalc dynamically
        return (
            (parseFloat(quantity) || 0) *
            (parseFloat(rate) || 0) *
            (1 - (parseFloat(discount) || 0) / 100) *
            (1 + (parseFloat(tax) || 0) / 100)
        ).toFixed(2);
    };



    const handleInputChange = (id, field, value) => {
        if (field === "item") {
            const product = resolveProductFromSelect(value);
            if (!product) return;

            if (product.product_type === "Variable Product") {
                setCurrentVariableProduct(product);
                setSelectedRowId(id);
                setShowVariationModal(true);
                return;
            }

            const updatedItems = items.map(item =>
                item.id === id ? rowFromProduct(item, product) : item
            );
            setItems(updatedItems);
            return;
        }

        const updatedItems = items.map(item =>
            item.id === id ? { ...item, [field]: value } : item
        );
        setItems(updatedItems);
    };


    const handleAddRow = () => {
        setItems([
            ...items,
            { id: Date.now(), item: null, quantity: "1", rate: "", discount: "", tax: "", amount: 0,hsn:"" },
        ]);
    };

    // const handleDeleteRow = (indexToRemove) => {
    //     const updatedItems = items.filter((_, index) => index !== indexToRemove);
    //     setItems(updatedItems);

    // };
    const handleDeleteRow = (id) => {
        const filtered = items.filter(row => row.id !== id);
        setItems(filtered);

    };

    console.log(items)

    return (
        <div className="oms-modal-form">
            {items.map((item) => (
                <div key={item.id} className="item-row">
                    <div className="oms-form-group">
                        <label htmlFor={`item-${item.id}`} className="oms-form-label">
                            Item
                        </label>
                        {/* <Select
                            options={itemsOptions}
                            // value={item.title}
                            value={
                                item.item
                                    ? {
                                        label: item.item.title,
                                        value: item.item,
                                    }
                                    : null
                            }

                         

                            onChange={(selectedOption) =>
                                handleInputChange(item.id, "item", selectedOption)
                            }
                            placeholder="Select Item"
                            id={`item-${item.id}`}
                        /> */}
                        <Select
                            options={itemsOptions}
                            value={
                                item.item?.title
                                    ? {
                                        label: item.item.title,
                                        value: item.item,
                                    }
                                    : item.item || null
                            }
                            onChange={(selectedOption) =>
                                selectedOption &&
                                handleInputChange(item.id, "item", selectedOption)
                            }
                            placeholder="Select Item"
                            id={`item-${item.id}`}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={itemSelectStyles}
                            isClearable
                            noOptionsMessage={() =>
                                itemsOptions.length
                                    ? "No matching products"
                                    : "No products — add under Products"
                            }
                        />

                    </div>
                    <div className="oms-form-group">
                        <label className="oms-form-label">
                            HSN
                        </label>
                        <input
                            type="text"
                            value={item.hsn || ""}
                            readOnly
                            className="readonly-input"
                            placeholder="HSN Code"
                        />
                    </div>

                    {/* { console.log(item.item.value.title)} */}
                    <div className="oms-form-group">
                        <label htmlFor={`quantity-${item.id}`} className="oms-form-label">
                            Quantity
                        </label>
                        <input
                            type="number"
                            id={`quantity-${item.id}`}
                            placeholder="Enter quantity"
                            value={item.quantity}
                            onChange={(e) =>
                                handleInputChange(item.id, "quantity", parseFloat(e.target.value))
                            }
                            onWheel={(e) => e.target.blur()}
                        />
                    </div>
                    <div className="oms-form-group">
                        <label htmlFor={`rate-${item.id}`} className="oms-form-label">
                            Rate
                        </label>
                        <input
                            type="number"
                            id={`rate-${item.id}`}
                            placeholder="Enter rate"
                            value={item.rate}
                            onChange={(e) =>
                                handleInputChange(item.id, "rate", parseFloat(e.target.value))
                            }
                            onWheel={(e) => e.target.blur()}
                        />
                    </div>
                    <div className="oms-form-group">
                        <label htmlFor={`discount-${item.id}`} className="oms-form-label">
                            Discount (%)
                        </label>
                        {/* <input
                            type="number"
                            id={`discount-${item.id}`}
                            name={`discount-${item.id}`}
                            placeholder="Enter discount"
                            value={item.discount}
                            // onChange={(e) =>
                            //     handleInputChange(item.id, "discount", parseFloat(e.target.value))
                            // }
                            onChange={(e) => handleInputChange(item.id, "discount", e.target.value)}

                            onBlur={(e) => {
                                const value = parseFloat(e.target.value);
                                if (isNaN(value) || value < 0 || value > 100) {
                                    toast.warning("Please enter a valid discount (0–100%)");
                                    handleInputChange(item.id, "discount", "");
                                } else {
                                    handleInputChange(item.id, "discount", value.toFixed(2));
                                }
                            }}

                            min="0"
                            max="100"
                        /> */}
                        <input
                            type="number"
                            name={`discount-${item.id}`}
                            id={`discount-${item.id}`}
                            placeholder="Enter discount"
                            value={item.discount}
                            step="any" // ✅ Allow decimal steps
                            onChange={(e) => handleInputChange(item.id, "discount", e.target.value)}
                            onBlur={(e) => {
                                const value = parseFloat(e.target.value);
                                if (isNaN(value) || value < 0 || value > 100) {
                                    toast.warning("Please enter a valid discount (0–100%)");
                                    handleInputChange(item.id, "discount", "");
                                } else {
                                    handleInputChange(item.id, "discount", parseFloat(value).toFixed(2));
                                }
                            }}
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            max="100"
                        />

                    </div>
                    <div className="oms-form-group">
                        <label htmlFor={`tax-${item.id}`} className="oms-form-label">
                            Tax (%)
                        </label>
                        <input
                            type="number"
                            id={`tax-${item.id}`}
                            placeholder="Enter tax"
                            value={item.tax}
                            onChange={(e) =>
                                handleInputChange(item.id, "tax", parseFloat(e.target.value))
                            }
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            max="100"
                        />
                    </div>
                    <div className="oms-form-group">
                        <label htmlFor={`amount-${item.id}`} className="oms-form-label">
                            Total Amount
                        </label>
                        <input
                            type="text"
                            id={`amount-${item.id}`}
                            value={calculateAmount(item.quantity, item.rate, item.discount, item.tax, item.amount, true)}
                            readOnly
                            className="readonly-input"
                        />

                    </div>
                    <button
                        type="button"
                        onClick={() => handleDeleteRow(item.id)}
                        className="mobile-delete-row-btn"
                    >
                        <i class="fa-solid fa-trash-can"></i>  Delete item
                    </button>
                </div>
            ))}
            <div className="item-table-actions">
                <button type="button" onClick={handleAddRow} className="add-row-btn">
                    Add more
                </button>
                <button
                    type="button"
                    onClick={onOpenAddProduct}
                    className="add-row-btn add-product-row-btn"
                >
                    Add product
                </button>
            </div>
        </div>
    );
};
